import { ReactElement } from 'react'
import CodeIcon from '@mui/icons-material/Code'
import AutoModeIcon from '@mui/icons-material/AutoMode'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import BuildCircleIcon from '@mui/icons-material/BuildCircle'
import SchoolIcon from '@mui/icons-material/School'
import StorefrontIcon from '@mui/icons-material/Storefront'

// All customer-facing marketing copy lives here so it can be edited without touching layout code.
// Employment history / education / certifications stay in resumeData.tsx.

export type Service = {
    id: string
    title: string
    shortTitle: string
    icon: ReactElement
    /** One-line result for the customer */
    summary: string
    description: string
    outcomes: string[]
    examples: string[]
    /** Preselected option on the contact form */
    contactTopic: string
    /** Optional link to real work that shows this service */
    example?: { label: string, to: string }
    /** Published starting price and what it covers */
    pricing?: { startingAt: string, scope: string, includes: string[], quotedSeparately: string }
}

export const services: Service[] = [
    {
        id: 'custom-software',
        contactTopic: 'New application',
        title: 'Custom Software Development',
        shortTitle: 'Custom Software',
        icon: <CodeIcon />,
        summary: 'Web applications, internal tools and APIs built to fit how your business actually works.',
        description:
            'When off-the-shelf software doesn’t fit, or you’re stitching together five tools to do one job, I build the application you need. Production-ready, documented, and maintainable by whoever comes after me.',
        outcomes: [
            'A working application your team or customers can use, not a prototype that needs rebuilding',
            'Secure sign-in, sensible permissions and data that’s backed up',
            'Code and infrastructure your future developers can understand',
        ],
        examples: [
            'New web applications',
            'Internal business tools and admin portals',
            'Dashboards and reporting',
            'APIs and integrations',
            'React / TypeScript frontends',
            'AWS serverless backends',
        ],
    },
    {
        id: 'small-business-websites',
        contactTopic: 'Small business website',
        title: 'Small Business Websites',
        shortTitle: 'Small Business Websites',
        icon: <StorefrontIcon />,
        summary: 'A professional, mobile-friendly website that helps customers find your business and get in touch.',
        description:
            'Most people look a business up on their phone before they call or visit. I build clean websites that look good on any screen, explain what you do in plain language, and make it easy for customers to reach you. I handle the setup and deployment too, so you don’t have to.',
        outcomes: [
            'A site that looks professional on phones, tablets and computers',
            'Clear ways for customers to call, email or send you a message',
            'Basic SEO setup, so search engines know what you do and where you are',
            'Setup and deployment handled for you',
        ],
        examples: [
            'Responsive design',
            'Contact forms',
            'Local SEO fundamentals',
            'Deployment',
        ],
        example: { label: 'See a live example: Rietow Family Dentistry', to: '/work#websites' },
        pricing: {
            startingAt: '$1,000',
            scope: 'A straightforward 3–5 page informational website.',
            includes: ['Mobile-friendly design', 'Basic SEO setup', 'Contact form', 'Deployment'],
            quotedSeparately: 'Custom functionality, ongoing maintenance, and third-party costs such as domain names, hosting or paid services are quoted separately.',
        },
    },
    {
        id: 'automation',
        contactTopic: 'Automation',
        title: 'Business Automation',
        shortTitle: 'Automation',
        icon: <AutoModeIcon />,
        summary: 'Replace repetitive manual work with reliable tools so your people can spend their time on things that need a person.',
        description:
            'Copying data between systems, re-keying spreadsheets, running the same report every Monday, emailing files back and forth. If someone on your team does it by hand on a schedule, there’s a good chance it can be automated.',
        outcomes: [
            'Hours of repetitive work handed off to software',
            'Fewer copy-paste mistakes',
            'Systems that talk to each other instead of through a person',
        ],
        examples: [
            'Workflow automation',
            'System-to-system integrations',
            'Scheduled jobs and data syncs',
            'Report and file generation',
            'Scripts and small services that save time',
        ],
    },
    {
        id: 'consulting',
        contactTopic: 'Architecture / consulting',
        title: 'Architecture & Technical Consulting',
        shortTitle: 'Architecture & Consulting',
        icon: <AccountTreeIcon />,
        summary: 'A senior engineer’s honest read on your system: what’s working, what’s fragile, and what it should cost to run.',
        description:
            'Before you build, scale or rewrite, get a second set of eyes. I review applications and AWS environments and tell you plainly where the risks, the unnecessary complexity and the wasted spend are, and what I’d do about them.',
        outcomes: [
            'A clear, prioritized list of what to fix and what to leave alone',
            'Architecture that fits your actual scale and budget',
            'Lower cloud costs where infrastructure is doing work it doesn’t need to',
        ],
        examples: [
            'Application and code reviews',
            'AWS / serverless architecture',
            'DynamoDB data modeling and access patterns',
            'Scalability and maintainability',
            'Technical debt assessment',
            'Cloud cost optimization',
        ],
    },
    {
        id: 'technical-rescue',
        contactTopic: 'Existing system / troubleshooting',
        title: 'Technical Rescue',
        shortTitle: 'Technical Rescue',
        icon: <BuildCircleIcon />,
        summary: 'For applications that are broken, expensive, hard to change, or that nobody on the team wants to touch.',
        description:
            'Maybe the original developer left. Maybe deploys are scary and bugs keep coming back. I dig in, find out what’s actually going on, stabilize things, and leave the system easier to work on than I found it.',
        outcomes: [
            'Root causes found and fixed, not just symptoms patched',
            'Deployments that are boring instead of stressful',
            'Logging and tests that tell you when something breaks',
        ],
        examples: [
            'Debugging production issues',
            'Stabilizing fragile systems',
            'Automated testing',
            'Logging and monitoring',
            'CI/CD and deployment pipelines',
            'Developer experience improvements',
        ],
    },
    {
        id: 'mentorship',
        contactTopic: 'Mentorship',
        title: 'Developer Mentorship',
        shortTitle: 'Mentorship',
        icon: <SchoolIcon />,
        summary: 'One-on-one guidance for students, aspiring developers and working engineers who want to grow faster.',
        description:
            'Practical mentorship from someone who still writes production code: code reviews, architecture, AWS, React, debugging, and the habits that make an engineer someone the team relies on.',
        outcomes: [
            'Faster progress than figuring it out alone',
            'More confidence making technical decisions',
            'Better code, reviewed by someone with real production experience',
        ],
        examples: [
            'One-on-one sessions',
            'Code reviews',
            'Architecture guidance',
            'React / TypeScript & AWS',
            'Career and professional development',
        ],
    },
]

/** "Sound familiar?" prompts on the homepage. Each maps to a service. */
export const painPoints: { text: string, serviceId: string }[] = [
    { text: 'Someone on your team spends hours a week copying data between spreadsheets and systems.', serviceId: 'automation' },
    { text: 'You need an internal tool or admin portal, but your developers are busy with the product.', serviceId: 'custom-software' },
    { text: 'Your AWS bill keeps climbing and nobody is quite sure why.', serviceId: 'consulting' },
    { text: 'The app works, mostly, but every change breaks something and deploys are stressful.', serviceId: 'technical-rescue' },
    { text: 'Your business needs a website, or the one you have is hard to use on a phone.', serviceId: 'small-business-websites' },
    { text: 'You’re studying CS, breaking into software, or already a developer, and want an experienced engineer in your corner.', serviceId: 'mentorship' },
]

export type CaseStudyCategory =
    | 'Application development & modernization'
    | 'Cloud architecture & security'
    | 'Automation, reliability & operations'

export type CaseStudy = {
    id: string
    title: string
    category: CaseStudyCategory
    /** Only set where I personally implemented or led the work; most projects were team efforts */
    role?: string
    problem: string
    work: string
    result: string
    tags: string[]
    /** Shown up front on the Work page (and the first four on the homepage); the rest sit in an expandable section */
    featured?: boolean
}

// Case studies come from full-time employment (mostly Black Widow Imaging; legacy-modernization is from an earlier role),
// not freelance clients. Keep them anonymized: no customer, project or repo names, no infrastructure specifics,
// no internal costs, and no invented metrics. Savings in dynamodb-cost are projected, not realized.
export const caseStudies: CaseStudy[] = [
    {
        id: 'app-modernization',
        title: 'Application Modernization & TypeScript Migration',
        category: 'Application development & modernization',
        role: 'Led the refactoring and TypeScript migration',
        problem: 'A production React web application and its React Native mobile app had grown inconsistent. Mixed patterns, untyped JavaScript and tangled logic made bugs hard to track down and new features slower to build than they should have been.',
        work: 'Led an extensive refactor of both applications, migrating them to TypeScript and reorganizing each codebase into a consistent, maintainable structure. Separated responsibilities, simplified overly complicated logic, introduced reusable components and patterns, and kept the mobile app integrated with the existing backend services so the same workflows worked across devices.',
        result: 'Cleaner, more consistent codebases with stronger type safety. Problems surface earlier and are easier to trace, and future work on web and mobile starts from a simpler foundation.',
        tags: ['React', 'React Native', 'TypeScript', 'Component architecture', 'Refactoring'],
        featured: true,
    },
    {
        id: 'auth-modernization',
        title: 'Authentication & Access Control Modernization',
        category: 'Cloud architecture & security',
        role: 'Implemented the Cognito integration',
        problem: 'A production application kept user roles and permissions in its own DynamoDB tables. That meant maintaining custom code, and extra complexity, just to manage who could sign in and what they could access.',
        work: 'Implemented Amazon Cognito to replace the homegrown approach with an AWS-managed identity service. User Pools handle sign-in and user management, while Identity Pools provide AWS credentials so access to resources is governed by IAM. Integrated it into the existing application and backend services.',
        result: 'Identity and access management in one central, standard place, with less custom authentication logic to maintain going forward.',
        tags: ['Cognito', 'User Pools', 'Identity Pools', 'IAM', 'DynamoDB', 'React', 'Lambda'],
        featured: true,
    },
    {
        id: 'dynamodb-cost',
        title: 'AWS / DynamoDB Cost Optimization',
        category: 'Cloud architecture & security',
        problem: 'A production serverless application was making more database writes than it needed to because of inefficient access patterns and indexing, which added infrastructure cost and complexity.',
        work: 'Analyzed the DynamoDB access patterns, indexes and the application workflows behind them. Identified redundant writes and places the design could be simpler, then developed an optimized approach that removed unnecessary database activity while keeping everything the application needed to do.',
        result: 'Identified opportunities for significant projected infrastructure savings, along with a simpler architecture that is easier to maintain long term.',
        tags: ['DynamoDB', 'Lambda', 'Python', 'Serverless', 'Cost optimization'],
        featured: true,
    },
    {
        id: 'react-aws-app',
        title: 'Production React + AWS Application',
        category: 'Application development & modernization',
        problem: 'The business needed a modern, responsive application and a reliable way to ship changes to it.',
        work: 'Built and maintained React / TypeScript frontends on a serverless AWS backend using Lambda, API Gateway, DynamoDB, S3 and CloudFront. Set up CI/CD automation for deployments and served as the team’s go-to resource for AWS architecture.',
        result: 'A production application with automated deployments and an architecture the team could maintain and extend.',
        tags: ['React', 'TypeScript', 'Lambda', 'API Gateway', 'S3', 'CloudFront', 'CI/CD'],
        featured: true,
    },
    {
        id: 'testing-reliability',
        title: 'Automated Testing & Application Reliability',
        category: 'Automation, reliability & operations',
        role: 'Introduced and maintained the test suites',
        problem: 'The applications had little automated testing, so every change carried risk and regressions were harder to catch before deployment.',
        work: 'Introduced automated testing across the applications I maintained: Jest for unit tests and Cypress for end-to-end tests that use the app the way a person would. Built and maintained suites that protect existing functionality, and improved debugging practices and logging where it helped.',
        result: 'A stronger foundation for reliability. Automated checks catch regressions earlier, problems are easier to debug, and the team can change existing code with more confidence.',
        tags: ['Jest', 'Cypress', 'React', 'TypeScript', 'CI/CD'],
        featured: true,
    },
    {
        id: 'internal-tools',
        title: 'Internal Tools & Self-Service Administration',
        category: 'Automation, reliability & operations',
        problem: 'Routine configuration changes, user administration and operational tasks all needed a developer to step in, which interrupted engineering work and slowed down support.',
        work: 'Built and maintained React administration applications backed by secure APIs, with interfaces for managing configuration, users and routine operational workflows. Validation and permissions make sure the right people can make the right changes.',
        result: 'Non-developer teams handle routine tasks on their own, with fewer interruptions for developers and smoother day-to-day operations.',
        tags: ['React', 'TypeScript', 'Material UI', 'Python', 'REST APIs', 'AWS'],
    },
    {
        id: 'real-time-updates',
        title: 'Real-Time Application Updates',
        category: 'Application development & modernization',
        problem: 'An operational web application needed to show live equipment and workflow updates, but people had to keep refreshing the page to see changes.',
        work: 'Integrated event-driven messaging between AWS services and the React frontend using MQTT over AWS IoT Core, and built the event handling and state updates so the interface responds to changes as they happen.',
        result: 'Near-real-time visibility into operational workflows, less reliance on manual refreshes, and a more responsive application.',
        tags: ['React', 'TypeScript', 'AWS IoT Core', 'MQTT', 'Event-driven'],
    },
    {
        id: 'media-workflows',
        title: 'Cloud Media Processing & Storage Workflows',
        category: 'Cloud architecture & security',
        problem: 'An application needed a dependable way to handle media uploads, storage, processing and delivery across several systems.',
        work: 'Developed and improved cloud-backed media workflows using object storage, secure API integrations and asynchronous processing on AWS, coordinating media operations across different parts of the application while keeping the architecture maintainable.',
        result: 'More reliable, maintainable media handling that supports automated downstream processing.',
        tags: ['S3', 'Lambda', 'API Gateway', 'Python', 'SQS', 'CloudFront'],
    },
    {
        id: 'legacy-modernization',
        title: 'Legacy Application Modernization',
        category: 'Application development & modernization',
        problem: 'At an earlier role, a legacy PHP web application was holding back the user experience and slowing feature development.',
        work: 'Contributed to the migration from the legacy PHP application to a modern React frontend, and built Lambda-based, event-driven microservices in Node.js alongside it.',
        result: 'An improved user experience and system performance on a platform the team could keep building on.',
        tags: ['React', 'PHP migration', 'Node.js', 'Event-driven', 'Lambda'],
    },
]

export type TechGroup = { name: string, items: string[] }

export const techStack: TechGroup[] = [
    { name: 'Frontend', items: ['React', 'TypeScript', 'JavaScript', 'React Native', 'Material UI'] },
    { name: 'Backend', items: ['Python', 'Node.js', 'Django', 'REST APIs', 'Event-driven services'] },
    { name: 'AWS / Cloud', items: ['Lambda', 'API Gateway', 'DynamoDB', 'Cognito', 'S3', 'CloudFront', 'SQS', 'SNS', 'IAM', 'CloudWatch', 'IoT Core'] },
    { name: 'Engineering', items: ['Architecture', 'Automated testing', 'Jest', 'Cypress', 'CI/CD', 'Docker'] },
]

export const processSteps: { title: string, body: string }[] = [
    { title: 'Tell me the problem', body: 'Send a few sentences about what you’re trying to build, fix or stop doing by hand. No formal spec needed.' },
    { title: 'We talk it through', body: 'A short conversation to understand your situation and whether I’m the right fit. If I’m not, I’ll tell you.' },
    { title: 'A clear plan', body: 'You get a plain-language plan with the scope, the approach and what it will take, before any work starts.' },
    { title: 'Build and hand off', body: 'I build it, keep you updated as I go, and hand over something documented that your team can own.' },
]

export type CredentialHighlight = { label: string, detail: string }

export const credentials: CredentialHighlight[] = [
    // TODO(Thomas): you mentioned 9–10 years depending on how dates are counted; resume dates show 2018 to present. Adjust if you want a specific number.
    { label: 'Since 2018', detail: 'Building production software professionally' },
    { label: 'AWS Certified', detail: 'Solutions Architect – Associate & Developer – Associate' },
    { label: 'U.S. Army veteran', detail: 'Discipline, ownership and getting it right' },
    { label: 'Full-stack', detail: 'From the React UI to the AWS infrastructure under it' },
]

export const employers = ['Black Widow Imaging', 'Chronotrack / Life Time', 'Ontario Systems / Finvi']

export const mentorship = {
    topics: [
        { title: 'Code reviews', body: 'Detailed, honest feedback on your real code: what works, what to change, and why.' },
        { title: 'Architecture', body: 'How to structure applications and services so they stay maintainable as they grow.' },
        { title: 'AWS & serverless', body: 'Lambda, API Gateway, DynamoDB, Cognito and friends, and when to use each one.' },
        { title: 'React & TypeScript', body: 'Component design, state management, typing, and keeping a frontend clean.' },
        { title: 'Debugging', body: 'A repeatable way to track down problems instead of guessing and hoping.' },
        { title: 'Automated testing', body: 'Writing tests that catch real bugs with Jest and Cypress, without slowing you down.' },
        { title: 'Problem solving', body: 'Breaking down vague tickets and big features into work you can actually start on.' },
        { title: 'Independence', body: 'Building the judgment and confidence to make decisions without waiting on someone else.' },
    ],
    audiences: [
        'College students earning a CS degree who want real-world experience alongside their coursework',
        'Anyone trying to break into software development and looking for a clear path in',
        'Junior and mid-level engineers who want to level up faster',
        'Bootcamp and self-taught developers making the jump to professional work',
        'Engineers moving into React, TypeScript or AWS',
        'Developers preparing for more ownership, or their next role',
    ],
    // No booking integration yet; every format goes through the contact form.
    formats: [
        {
            title: 'Single session',
            price: '$75',
            priceNote: '60-minute session',
            body: 'One focused session on a specific problem, codebase or decision. Good for getting unstuck.',
            details: [
                { label: 'Bring', value: 'The code, error or decision you’re stuck on' },
                { label: 'You get', value: 'Working through it together, and a clear next step' },
            ],
        },
        {
            title: 'Ongoing mentorship',
            price: '$65',
            priceNote: 'per 60-minute session',
            body: 'Regular one-on-one sessions with questions over email in between. Best for steady growth over a few months.',
            details: [
                { label: 'Cadence', value: 'Weekly or every other week' },
                { label: 'Between sessions', value: 'Quick questions over email; bigger topics are saved for the next session' },
            ],
        },
        {
            title: 'Code & architecture review',
            price: 'From $125',
            priceNote: 'scope agreed beforehand',
            body: 'Send me a repository or design and get a written review plus a walkthrough call.',
            details: [
                { label: 'You send', value: 'A repository, pull request or design doc' },
                { label: 'You get', value: 'A written review and a call to walk through it' },
            ],
        },
    ],
    background: [
        'Mentored interns through a formal mentorship program as they moved into professional engineering work',
        'Worked with newer soldiers as a Specialist in the U.S. Army',
        'Served as a peer mentor for Anatomy & Physiology at IUPUI',
        'Came into software through a bootcamp myself, so I know how that path feels',
    ],
}

export const contactTopics = [
    'Small business website',
    'New application',
    'Automation',
    'Existing system / troubleshooting',
    'Architecture / consulting',
    'Mentorship',
    'Other',
]
